/** A file's stage as one chip - ADR-0212: Received, Checked, Read, Matched, Accepted, and the ways a
 * file stops.
 *
 * **A presentation of the document's real state and nothing else** (the trail's rule, `trail.ts`):
 * nothing here moves a document. The five stages are the furthest step the file has reached, and
 * each is a word, a tone and a glyph so no state rides on colour alone.
 *
 * - `received` / `validated`: *Received* is the row before its checks ran; *Checked* is a file whose
 *   type, size and encryption were accepted (`stages.py` is explicit that this is **not** a virus
 *   scan, which is why the word is not "scanned").
 * - `extracting`: *Reading*, the one moment the system is on it.
 * - `awaiting_review`: *Read* when the values are found and nobody has said which site; *Matched*
 *   when the uploader named a site. The site is the uploader's **hint**, not a match the product
 *   made (SEC-05), so Matched means "stated", and the person still chooses at review.
 * - `verified`: *Accepted*, a person's act.
 * - `quarantined`: *Refused*, carrying the server's refusal code as its own chip.
 */

import type { DocumentRow } from "../../api/evidence";
import type { ChipTone } from "../../components/ui";
import { stateWord } from "../../lib/documentStages";

export type StageKey =
  | "received"
  | "checked"
  | "reading"
  | "read"
  | "matched"
  | "accepted"
  | "refused"
  | "set_aside"
  | "replaced";

export interface StageChipModel {
  key: StageKey;
  word: string;
  tone: ChipTone;
}

type StageInput = Pick<DocumentRow, "state" | "facility_hint_name" | "facility_hint_id">;

export function stageChipOf(document: StageInput): StageChipModel {
  switch (document.state) {
    case "received":
      return { key: "received", word: stateWord("received"), tone: "neutral" };
    case "validated":
      return { key: "checked", word: stateWord("validated"), tone: "info" };
    case "extracting":
      return { key: "reading", word: stateWord("extracting"), tone: "info" };
    case "awaiting_review":
      return document.facility_hint_id || document.facility_hint_name
        ? { key: "matched", word: "Matched", tone: "warn" }
        : { key: "read", word: "Read", tone: "warn" };
    case "verified":
      return { key: "accepted", word: stateWord("verified"), tone: "ok" };
    case "quarantined":
      return { key: "refused", word: stateWord("quarantined"), tone: "crit" };
    case "rejected":
      return { key: "set_aside", word: stateWord("rejected"), tone: "crit" };
    default:
      return { key: "replaced", word: stateWord("superseded"), tone: "neutral" };
  }
}

/** The refusal code as a short phrase for a chip. The server's `refusal_code` is the fact and the
 * remedy is derived from it (DOC-01); this only names the code for a person, and an unlisted code
 * shows itself with the underscores removed rather than being hidden. */
const REFUSAL_WORDS: Readonly<Record<string, string>> = {
  empty: "Empty file",
  too_large: "Too large",
  type_not_allowed: "Type not accepted",
  unrecognised: "Not recognised",
  type_mismatch: "Wrong file type",
  encrypted: "Password protected",
  active_content: "Active content",
  duplicate: "Already held",
};

export function refusalWord(code: string | null | undefined): string | null {
  if (!code) return null;
  return REFUSAL_WORDS[code] ?? code.replace(/_/g, " ");
}
